import React, { useEffect, useState } from "react";
import { HardDrive } from "lucide-react";
import api from "../lib/api";

/**
 * Small admin-only chip that displays current GridFS usage and a soft quota.
 * Polls every 30 s and after every successful upload event (via the optional
 * `refreshKey` prop — bump it to trigger a re-fetch).
 */
export default function StorageMeter({ refreshKey = 0 }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api.admin
        .storage()
        .then((d) => alive && setData(d))
        .catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [refreshKey]);

  if (!data) return null;
  const pct = Math.max(2, Math.min(100, data.percent));
  const danger = pct >= 90;
  const warning = pct >= 70 && pct < 90;
  const barColor = danger
    ? "bg-red-500"
    : warning
      ? "bg-amber-500"
      : "bg-[var(--vermillion)]";

  return (
    <div
      className="flex items-center gap-2 rounded-full bg-white/90 md:backdrop-blur px-3 py-1.5 border border-[var(--ink)]/10 card-stack text-[11px]"
      title={`${data.files} files · ${data.humanUsed} of ${data.humanQuota} used`}
    >
      <HardDrive className="w-3.5 h-3.5 text-ink-soft" />
      <div className="hidden sm:block tabular-nums whitespace-nowrap">
        <span className="font-medium">{data.humanUsed}</span>
        <span className="text-ink-soft/60"> / {data.humanQuota}</span>
      </div>
      <div className="sm:hidden tabular-nums whitespace-nowrap font-medium">
        {data.humanUsed}
      </div>
      <div className="w-16 sm:w-24 h-1.5 rounded-full bg-[var(--paper-deep)] overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
